// ---------------------------------------------------------------------------
// Email Notifications Diagnostic Probe (frontend-only, read-only)
// ---------------------------------------------------------------------------
// Purpose
//   Replace the generic "Failed to send a request to the Edge Function"
//   diagnostic in System Health with a CATEGORIZED probe that reports the
//   EXACT cause and EXACT manual next step.
//
// Categories (ordered by specificity)
//   pass                      — function reachable + Resend accepted dry-run.
//   function-missing          — HTTP 404 from /functions/v1/send-test-email
//                               (function not deployed on this project).
//   secret-missing            — function ran but reports RESEND_API_KEY is
//                               not set in backend secrets.
//   sender-not-verified       — Resend rejected with a domain-not-verified /
//                               from-not-allowed signal.
//   resend-rejected           — Resend rejected for some other reason
//                               (rate-limit, invalid API key, etc.).
//   function-error            — function returned 5xx with no recognized
//                               classification body.
//   function-unreachable      — supabase-js threw "Failed to send a request
//                               to the Edge Function" AND the direct HTTP
//                               probe also failed (cold start, network,
//                               CORS, project paused).
//   unknown                   — none of the above patterns matched.
//
// Strict guarantees
//   • Read-only. Always sends `{ dryRun: true, probe: true }` so no real
//     email is ever delivered by this probe.
//   • Pure frontend. NO edge function code is created or modified here.
//   • No mutation. No DB write. No storage write. No auth state change.
//   • Surfaces the EXACT cause and EXACT manual fix the operator must do.
//
// Intentionally NOT touched by this module:
//   payments, pricing, uploads, family graph, invites, relationship engine,
//   identity system, AI flags, schema, RLS, storage buckets.
// ---------------------------------------------------------------------------

import { supabase } from '@/lib/supabase';

// ---------------------------------------------------------------------------
// Configuration — must match src/lib/supabase.ts
// ---------------------------------------------------------------------------
// The function name System Health currently calls. Confirmed by grep —
// every email pipeline (password reset, magic link, this probe) routes
// through the SAME edge function. If you ever rename the function, change
// it here AND in AuthContext.sendPasswordReset / sendMagicLink.
export const EMAIL_FUNCTION_NAME = 'send-test-email';

// Same project ref hard-coded in src/lib/supabase.ts. Kept local instead
// of re-imported to avoid a circular dependency and so this diagnostic
// remains self-contained.
const SUPABASE_URL = 'https://vadsiqsuomscnuiuyreo.supabase.co';
const SUPABASE_ANON_KEY =
  'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InZhZHNpcXN1b21zY251aXV5cmVvIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzgxMDQzNTEsImV4cCI6MjA5MzY4MDM1MX0.6GAL26u3kRl4C94_Xcex-feHYQZ9L4yf46r6pUIS1k8';

// Verified senders for the kinscircleapp.com domain. The probe accepts ANY
// of these as a "verified-sender" answer; it surfaces the canonical one
// (marionmoyn@) when the function reports an unverified sender.
// Order matters: the canonical default sender MUST be first.
export const VERIFIED_SENDERS = [
  'marionmoyn@kinscircleapp.com',
  'support@kinscircleapp.com',
  'noreply@kinscircleapp.com',
  'hello@kinscircleapp.com',
];
export const CANONICAL_SENDER = 'marionmoyn@kinscircleapp.com';


// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------
export type EmailDiagnosticCategory =
  | 'pass'
  | 'function-missing'
  | 'secret-missing'
  | 'sender-not-verified'
  | 'resend-rejected'
  | 'function-error'
  | 'function-unreachable'
  | 'unknown';

export type EmailDiagnosticSeverity = 'pass' | 'warn' | 'fail';

export interface EmailDiagnosticResult {
  category: EmailDiagnosticCategory;
  /**
   * Suggested System Health badge severity.
   *   pass  → green "Working"
   *   warn  → yellow "Warning"      (probe inconclusive, retry suggested)
   *   fail  → red "Failing"         (verified failure with manual fix)
   */
  severity: EmailDiagnosticSeverity;
  /** One-line headline for the System Health card. */
  detail: string;
  /** Exact manual step the operator should take next, or null if pass. */
  nextStep: string | null;
  /** Raw HTTP status seen on the direct probe (when available). */
  httpStatus: number | null;
  /** Best guess at sender address the function used / rejected. */
  senderSeen: string | null;
  /** Function name actually invoked (for operator visibility). */
  functionName: string;
  /** Project ref the probe targeted (for operator visibility). */
  projectRef: string;
  /** Raw error message from supabase-js, if any. Surfaced for support. */
  rawError: string | null;
  /** Raw response body parsed as JSON, if any. Surfaced for support. */
  rawBody: unknown;
}

// ---------------------------------------------------------------------------
// Body / error pattern matchers
// ---------------------------------------------------------------------------

const norm = (s: unknown): string =>
  typeof s === 'string' ? s.toLowerCase() : '';

function bodyMentionsMissingResendKey(body: any): boolean {
  if (!body) return false;
  const blob = norm(JSON.stringify(body));
  return (
    blob.includes('resend_api_key') ||
    (blob.includes('api key') &&
      (blob.includes('missing') || blob.includes('not set') || blob.includes('not configured'))) ||
    blob.includes('missing resend api') ||
    blob.includes('no resend api key')
  );
}

function bodyMentionsUnverifiedSender(body: any): { hit: boolean; senderSeen: string | null } {
  if (!body) return { hit: false, senderSeen: null };
  const blob = norm(JSON.stringify(body));
  const hit =
    (blob.includes('domain') && (blob.includes('not verified') || blob.includes('unverified') || blob.includes('verify'))) ||
    blob.includes('the from address is not a verified') ||
    blob.includes('from address must') ||
    blob.includes('sender is not verified') ||
    blob.includes('domain is not verified');
  let senderSeen: string | null = null;
  if (typeof body === 'object' && body !== null) {
    const candidate =
      (body as any).from ?? (body as any).sender ?? (body as any).fromAddress ?? null;
    if (typeof candidate === 'string') senderSeen = candidate;
  }
  return { hit, senderSeen };
}

function bodyMentionsResendRejection(body: any): boolean {
  if (!body) return false;
  const blob = norm(JSON.stringify(body));
  return (
    blob.includes('resend') &&
    (blob.includes('rejected') ||
      blob.includes('invalid api key') ||
      blob.includes('rate limit') ||
      blob.includes('rate_limit') ||
      blob.includes('unauthorized') ||
      blob.includes('forbidden') ||
      blob.includes('401') ||
      blob.includes('403'))
  );
}

/**
 * Distinguish "RESEND_API_KEY is set but the key value is invalid / rotated /
 * revoked" from a generic Resend rejection. Resend's 401 response for a bad
 * key is very specific: it contains "API key is invalid" (or
 * "name":"validation_error" + 401), and the function will surface that.
 *
 * This is the case the operator hit on 2026-05-10: the secret existed but
 * had been rotated on the Resend side, so apiKeyPresent: true at the
 * function level but Resend itself rejected with 401.
 */
function bodyMentionsInvalidResendKey(body: any): boolean {
  if (!body) return false;
  const blob = norm(JSON.stringify(body));
  return (
    blob.includes('api key is invalid') ||
    blob.includes('invalid api key') ||
    (blob.includes('validation_error') && blob.includes('401')) ||
    (blob.includes('resend') && blob.includes('401') && blob.includes('api key')) ||
    (blob.includes('unauthorized') && blob.includes('resend'))
  );
}


function errorLooksLikeFunctionUnreachable(message: string): boolean {
  const m = message.toLowerCase();
  return (
    m.includes('failed to send a request to the edge function') ||
    m.includes('failed to fetch') ||
    m.includes('network') ||
    m.includes('aborted')
  );
}

// ---------------------------------------------------------------------------
// Direct HTTP probe — used to distinguish "function not deployed" (HTTP 404)
// from generic "supabase-js threw a fetch error" so the operator gets the
// right next step.
// ---------------------------------------------------------------------------
async function directHttpProbe(): Promise<{
  ok: boolean;
  status: number | null;
  body: unknown;
  rawText: string | null;
  fetchError: string | null;
}> {
  const url = `${SUPABASE_URL}/functions/v1/${EMAIL_FUNCTION_NAME}`;
  try {
    // Read the live access token if available, fall back to anon key.
    let bearer = SUPABASE_ANON_KEY;
    try {
      const { data } = await supabase.auth.getSession();
      if (data?.session?.access_token) bearer = data.session.access_token;
    } catch {
      /* fall through to anon key */
    }
    const res = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'apikey': SUPABASE_ANON_KEY,
        'Authorization': `Bearer ${bearer}`,
      },
      body: JSON.stringify({ dryRun: true, probe: true, source: 'system-health-direct' }),
    });
    const rawText = await res.text();
    let body: unknown = null;
    try {
      body = rawText ? JSON.parse(rawText) : null;
    } catch {
      body = rawText;
    }
    return { ok: res.ok, status: res.status, body, rawText, fetchError: null };
  } catch (err: any) {
    return {
      ok: false,
      status: null,
      body: null,
      rawText: null,
      fetchError: err?.message || 'fetch threw',
    };
  }
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------
/**
 * Run the categorized email-pipeline diagnostic.
 *
 * Always safe — sends `dryRun: true` so no message is ever delivered by
 * this probe. Returns the EXACT category and EXACT manual next step.
 */
export async function runEmailDiagnostic(): Promise<EmailDiagnosticResult> {
  const baseResult = (
    overrides: Partial<EmailDiagnosticResult>,
  ): EmailDiagnosticResult => ({
    category: 'unknown',
    severity: 'warn',
    detail: 'Email diagnostic could not be classified.',
    nextStep: null,
    httpStatus: null,
    senderSeen: null,
    functionName: EMAIL_FUNCTION_NAME,
    projectRef: 'vadsiqsuomscnuiuyreo',
    rawError: null,
    rawBody: null,
    ...overrides,
  });

  // Step 1 — supabase-js invoke (the canonical SDK call System Health used
  // before this fix).
  let sdkError: { message: string; context?: any } | null = null;
  let sdkData: any = null;
  try {
    const { data, error } = await supabase.functions.invoke(EMAIL_FUNCTION_NAME, {
      body: { dryRun: true, probe: true, source: 'system-health' },
    });
    if (error) {
      sdkError = { message: error.message || 'invoke error', context: (error as any).context };
    } else {
      sdkData = data;
    }
  } catch (err: any) {
    sdkError = { message: err?.message || 'invoke threw' };
  }

  // Step 2 — direct HTTP probe in parallel-style (always run, cheap; gives
  // us the actual HTTP status that supabase-js obscures).
  const direct = await directHttpProbe();

  // ─────────────────────────────────────────────────────────────────────
  // Classification
  // ─────────────────────────────────────────────────────────────────────

  // (a) Function not deployed — HTTP 404 from the direct probe is the
  //     definitive signal. supabase-js can hide this as "Failed to send
  //     a request" so we always trust the direct probe here.
  if (direct.status === 404) {
    return baseResult({
      category: 'function-missing',
      severity: 'fail',
      httpStatus: 404,
      detail: `Edge function "${EMAIL_FUNCTION_NAME}" is NOT deployed on this Supabase project.`,
      nextStep:
        `Deploy the edge function. In your Supabase project (ref vadsiqsuomscnuiuyreo) ` +
        `run: supabase functions deploy ${EMAIL_FUNCTION_NAME}. ` +
        `Then re-run this check.`,
      rawError: sdkError?.message ?? null,
      rawBody: direct.body,
    });
  }

  // (b) Auth/permissions issue at the gateway — distinct from a deployed
  //     function returning 401 from inside (Resend rejection). Treat this
  //     as a function-error so the operator checks JWT verification config.
  if (direct.status === 401 || direct.status === 403) {
    return baseResult({
      category: 'function-error',
      severity: 'fail',
      httpStatus: direct.status,
      detail:
        `Edge function "${EMAIL_FUNCTION_NAME}" rejected the request at the gateway ` +
        `(HTTP ${direct.status}). The function is deployed but not callable from this client.`,
      nextStep:
        `Open Supabase → Edge Functions → ${EMAIL_FUNCTION_NAME} → Settings and confirm ` +
        `"Verify JWT" matches how you call this function. If the function is meant to ` +
        `accept the anon key, set Verify JWT = false. If it requires a logged-in user, ` +
        `make sure you're signed in before re-running this check.`,
      rawError: sdkError?.message ?? null,
      rawBody: direct.body,
    });
  }

  // (c) Function ran (HTTP 200/2xx OR 5xx with a JSON body) — inspect the
  //     payload for the specific Resend / sender / secret signals.
  const inspectionBody =
    sdkData ?? (direct.body !== null && direct.body !== undefined ? direct.body : null);

  if (inspectionBody && typeof inspectionBody === 'object') {
    const body: any = inspectionBody;

    // (c1) Function explicitly says secret missing.
    if (bodyMentionsMissingResendKey(body)) {
      return baseResult({
        category: 'secret-missing',
        severity: 'fail',
        httpStatus: direct.status ?? 200,
        detail:
          `RESEND_API_KEY is not configured in the edge function's backend secrets. ` +
          `The function is deployed but cannot reach Resend.`,
        nextStep:
          `Paste your Resend API key into Supabase → Project Settings → Edge Functions → ` +
          `Secrets, with the EXACT key name RESEND_API_KEY. ` +
          `Get the key from https://resend.com/api-keys (use a server-scoped key, not a ` +
          `client one). Then re-run this check. The key never appears in this app's ` +
          `frontend code.`,
        rawBody: body,
      });
    }

    // (c2) Function reports a sender / domain verification problem.
    const senderCheck = bodyMentionsUnverifiedSender(body);
    if (senderCheck.hit) {
      return baseResult({
        category: 'sender-not-verified',
        severity: 'fail',
        httpStatus: direct.status ?? 200,
        senderSeen: senderCheck.senderSeen,
        detail:
          `Resend rejected the sender${senderCheck.senderSeen ? ` "${senderCheck.senderSeen}"` : ''} ` +
          `because the kinscircleapp.com domain has not finished verification on this Resend account.`,
        nextStep:
          `Open https://resend.com/domains, confirm kinscircleapp.com shows "Verified" ` +
          `(SPF + DKIM + DMARC all green). If it shows "Pending", DNS may have propagated ` +
          `but Resend has not re-checked — click "Verify" in Resend. Then ensure the ` +
          `edge function's FROM address is one of: ${VERIFIED_SENDERS.join(', ')} ` +
          `(the canonical one is ${CANONICAL_SENDER}).`,
        rawBody: body,
      });
    }

    // (c3a) Resend specifically rejected with "API key is invalid" — this
    //       is the rotated/expired/revoked-key case. The secret IS set in
    //       Supabase but the value no longer matches a live key on Resend.
    //       This needs the "rotate-and-rebind" path, not the "paste a key
    //       for the first time" path, so we surface a distinct message.
    if (bodyMentionsInvalidResendKey(body)) {
      return baseResult({
        category: 'resend-rejected',
        severity: 'fail',
        httpStatus: direct.status ?? 200,
        detail:
          `Resend returned 401 "API key is invalid". The RESEND_API_KEY secret IS set in ` +
          `Supabase, but the value has been rotated, revoked, or never matched a live key on ` +
          `the Resend account that owns kinscircleapp.com.`,
        nextStep:
          `MANUAL FIX (the assistant cannot rotate this for you — only you can paste the ` +
          `new key into Supabase): ` +
          `1) Open https://resend.com/api-keys and create a NEW server-scoped key (full ` +
          `access) on the Resend account that owns kinscircleapp.com. Copy the key ONCE ` +
          `(it starts with re_…). ` +
          `2) Open https://supabase.com/dashboard/project/vadsiqsuomscnuiuyreo/settings/functions ` +
          `→ "Edge Function Secrets" → find RESEND_API_KEY → click "Edit" → paste the new ` +
          `value → Save. The secret name MUST be exactly RESEND_API_KEY (no prefix, no ` +
          `quotes). ` +
          `3) The function "${EMAIL_FUNCTION_NAME}" picks up the new secret on its next ` +
          `cold start automatically — no redeploy required. To force an immediate refresh ` +
          `you can hit "Redeploy" on the function page. ` +
          `4) Re-run this check. You should see category=pass, apiKeyPresent=true, ` +
          `from=${CANONICAL_SENDER}. ` +
          `DO NOT paste the key into chat or any frontend file — it must only live in the ` +
          `Supabase secrets store.`,
        rawBody: body,
      });
    }

    // (c3) Resend rejected for any other reason (rate-limit, suspended, …).
    if (bodyMentionsResendRejection(body)) {
      return baseResult({
        category: 'resend-rejected',
        severity: 'fail',
        httpStatus: direct.status ?? 200,
        detail:
          `Resend rejected the dry-run probe. The edge function is deployed and reachable, ` +
          `but Resend itself returned an error.`,
        nextStep:
          `Open https://resend.com/emails (Logs tab) and find the most recent rejection ` +
          `for kinscircleapp.com. The exact reason will appear there (rate-limit, ` +
          `suspended account, sandbox restriction, etc.). Fix it on the Resend side and ` +
          `re-run this check.`,
        rawBody: body,
      });
    }


    // (c4) Body shape says ok=true → success.
    if (body.ok === true || body.delivered === true || body.dryRun === true) {
      return baseResult({
        category: 'pass',
        severity: 'pass',
        httpStatus: direct.status ?? 200,
        senderSeen:
          typeof body.from === 'string'
            ? body.from
            : typeof body.sender === 'string'
            ? body.sender
            : null,
        detail:
          `Email pipeline reachable. ${EMAIL_FUNCTION_NAME} accepted the dry-run probe ` +
          `and Resend connectivity is OK.`,
        nextStep: null,
        rawBody: body,
      });
    }

    // (c5) Body shape says ok=false but we couldn't classify the reason.
    if (body.ok === false || body.error) {
      return baseResult({
        category: 'function-error',
        severity: 'fail',
        httpStatus: direct.status ?? 200,
        detail:
          `Edge function "${EMAIL_FUNCTION_NAME}" reported an error: ` +
          `${typeof body.error === 'string' ? body.error : 'no message'}.`,
        nextStep:
          `Open Supabase → Edge Functions → ${EMAIL_FUNCTION_NAME} → Logs and read the ` +
          `most recent invocation. The full stack trace will appear there. Common causes: ` +
          `missing secret (RESEND_API_KEY), invalid sender address, or a runtime error in ` +
          `the function code.`,
        rawError: typeof body.error === 'string' ? body.error : null,
        rawBody: body,
      });
    }
  }

  // (d) Function returned 5xx with no JSON we can classify.
  if (direct.status !== null && direct.status >= 500) {
    return baseResult({
      category: 'function-error',
      severity: 'fail',
      httpStatus: direct.status,
      detail:
        `Edge function "${EMAIL_FUNCTION_NAME}" returned HTTP ${direct.status} with no ` +
        `structured error body.`,
      nextStep:
        `Open Supabase → Edge Functions → ${EMAIL_FUNCTION_NAME} → Logs and read the ` +
        `most recent invocation for the full stack trace. If the function is throwing on ` +
        `startup, check that RESEND_API_KEY is set in Project Settings → Edge Functions → ` +
        `Secrets.`,
      rawError: sdkError?.message ?? null,
      rawBody: direct.body,
    });
  }

  // (e) supabase-js threw "Failed to send a request" AND the direct probe
  //     also failed → the function is unreachable. Could be: function not
  //     deployed (but no 404 returned, which means project is paused / DNS
  //     glitch), CORS, or cold-start beyond the SDK timeout.
  if (sdkError && errorLooksLikeFunctionUnreachable(sdkError.message) && !direct.ok) {
    return baseResult({
      category: 'function-unreachable',
      severity: 'fail',
      httpStatus: direct.status,
      detail:
        `Could not reach edge function "${EMAIL_FUNCTION_NAME}" at all. ` +
        `Both the supabase-js SDK and a direct HTTPS probe failed.`,
      nextStep:
        `1) Confirm the function is DEPLOYED: Supabase → Edge Functions → ` +
        `${EMAIL_FUNCTION_NAME} should appear in the list. If it is missing, run ` +
        `\`supabase functions deploy ${EMAIL_FUNCTION_NAME}\`. ` +
        `2) Confirm the project is not PAUSED: Supabase → Project Settings → General. ` +
        `3) Confirm your network is not blocking https://${SUPABASE_URL.replace('https://', '')}/functions/v1/. ` +
        `Re-run this check after each step.`,
      rawError: sdkError.message,
      rawBody: direct.body,
    });
  }

  // (f) supabase-js error we can't classify, but direct probe was fine.
  if (sdkError) {
    return baseResult({
      category: 'unknown',
      severity: 'warn',
      httpStatus: direct.status,
      detail: `supabase-js reported: ${sdkError.message}.`,
      nextStep:
        `Re-run this check. If it keeps failing, open the browser console for the full ` +
        `error and check Supabase → Edge Functions → ${EMAIL_FUNCTION_NAME} → Logs.`,
      rawError: sdkError.message,
      rawBody: inspectionBody ?? direct.body,
    });
  }

  // (g) Direct probe succeeded with a 2xx but body shape was empty / unknown.
  if (direct.ok) {
    return baseResult({
      category: 'pass',
      severity: 'pass',
      httpStatus: direct.status,
      detail:
        `Email pipeline reachable. ${EMAIL_FUNCTION_NAME} returned HTTP ${direct.status} ` +
        `(empty body — function may not echo dry-run results).`,
      nextStep: null,
      rawBody: direct.body,
    });
  }

  // (h) Final fallback — nothing matched.
  return baseResult({
    category: 'unknown',
    severity: 'warn',
    httpStatus: direct.status,
    detail: 'Email diagnostic returned an unrecognized response shape.',
    nextStep:
      `Re-run this check. If it keeps returning this state, open Supabase → ` +
      `Edge Functions → ${EMAIL_FUNCTION_NAME} → Logs for the most recent invocation ` +
      `and check the response body shape.`,
    rawError: sdkError?.message ?? direct.fetchError ?? null,
    rawBody: inspectionBody ?? direct.body,
  });
}
